import { z } from 'zod';
import { SLOT_REF_MAX_LENGTH } from '../utils/fillRef.util';
import { ATTENDANCE_STATES } from '../constants/dutyEvent.constant';
import { ARRANGEMENT_KINDS, ARRANGEMENT_LIMITS, calendarDateSchema } from './arrangement.schema';
import { ASSIGNMENT_STATUSES, FILL_STATES } from '../constants/fillState.constant';
import { COPY_REPEATS_SKIP_REASONS } from '../constants/copyRepeats.constant';
import { RAISER_LEVELS } from '../constants/raiserLevel.constant';
import { fillCommandFamilySchema } from './commandSeat.schema';
import { createPaginationSchema, objectIdSchema } from './common.schema';
import { showLifecycleSchema } from './lifecycle.schema';
import { officerSummarySchema } from './officer.schema';
import { orgUnitSummarySchema } from './orgUnit.schema';
import { rankSummarySchema } from './rank.schema';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  FULFILMENT — putting NAMES against an order (PA_REQUIREMENTS §5.3, §6.2). [P4]
 *
 *  ⚠️ EMPTY SLOTS ARE REAL ROWS. §4.2: an allocation of "3 UB Men from PS-A" materialises
 *  three assignment rows at publish, each with no officer. That is what makes the fill UI a
 *  grid of chips rather than a repeater, makes "8 of 12 named" a COUNT rather than a
 *  calculation, and gives a swap a stable identity to attach to.
 *
 *  ⚠️ A SWAP NEVER MUTATES. The old row becomes `SWAPPED_OUT` carrying its reason and a
 *  pointer to what replaced it, and a new row is inserted. The order sheet renders active
 *  rows; the audit view renders the chain. So `officerId` on a row is a historical fact
 *  once written, never a field that gets edited.
 *
 *  ⚠️ AND NOTHING HERE COUNTS A COMMAND ROLE. `requiredCount` and `namedCount` are FORCE
 *  ONLY — FR-5.2.6b, closing OD-9, because everybody assumes command counts toward force.
 *  There is no parameter in this file through which a command role could reach a total.
 *
 *  ⚠️ NO CONFLICT DETECTION, ANYWHERE. FR-5.3.3 closes OD-21: the CP team runs a complete
 *  offline record and handles double-booking themselves. Nothing in this contract carries a
 *  clash, and `api/tests/integration/conflictDeferred.test.ts` is what keeps it that way —
 *  read its header before adding one, because its messages have a redaction rule.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

// ── One assignment ───────────────────────────────────────────────────────────────

export const slotRefSchema = z.string().trim().min(1).max(SLOT_REF_MAX_LENGTH);

export const assignmentSchema = z.object({
  id: objectIdSchema,
  slotRef: slotRefSchema,
  /** The officer named, or `null` — which IS the empty slot, not the absence of a row. */
  officer: officerSummarySchema.nullable(),
  assignedAt: z.iso.datetime().nullable(),
  status: z.enum(ASSIGNMENT_STATUSES),
  /** Set on the row a swap retired, so the chain can be walked forwards. */
  replacedByAssignmentId: objectIdSchema.nullable(),
  swapReason: z.string().nullable(),
});

// ── The fill screen's read ───────────────────────────────────────────────────────

export const fillSlotSchema = z.object({
  slotRef: slotRefSchema,
  ordinal: z.number().int().min(0),
  /** `null` when the requirement grew after publish and nothing has claimed the slot yet. */
  assignment: assignmentSchema.nullable(),
  /**
   * ─── ⚠️ [C3c §7] PRESENT ONLY ONCE THE DUTY HAS ELAPSED, AND ITS PRESENCE IS THE SIGNAL ────
   *
   *  §6's own test — "does a caller who will never receive a value have to render something?" —
   *  is answered NO here, which is the case [P6d]'s optional rule exists for: a LIVE duty renders
   *  no attendance treatment at all, so the key's absence says "the derivation did not run"
   *  rather than "we do not know". A required nullable would put a key in every payload of the
   *  hot path — twelve names in ninety seconds — for a fact almost every one of them lacks.
   *
   *  ⚠️ AND ABSENCE FAILS SAFE. No key means no wash, so a screen can never claim somebody was
   *  absent because a field went missing.
   *
   *  ⚠️ IT IS ON THE **SLOT**, NOT ON `assignmentSchema`. Attendance is keyed
   *  `(deployment_id, subject_officer_id)` — a fact about a person on a duty — while an
   *  assignment row is the historical record and a swapped-out one has no attendance at all.
   */
  attendance: z.enum(ATTENDANCE_STATES).optional(),
});

export const fillAllocationSchema = z.object({
  allocKey: z.string(),
  division: orgUnitSummarySchema.nullable(),
  /** `null` means "any unit under this division" — the fast path, and most rows use it. */
  unit: orgUnitSummarySchema.nullable(),
  /**
   * ⚠️ [amended C1f §1b/§1e] WHICH ROW THIS ONE WAS SPLIT OUT OF, OR `null`.
   *
   * Until C1f the sheet carried no delegation marker at all, so a client could not tell a row
   * a division had handed to a station from one the CP office had addressed to that station
   * directly. §1b needs the difference (the Name/Delegate control is offered only while the
   * post is untouched — no names AND no delegations) and so does §1e (once delegated, the
   * control has nothing to switch back to and disappears).
   *
   * ⚠️ REQUIRED KEY, NULLABLE VALUE — §6's own test answered "yes". Every row renders
   * something either way and this decides which, so it is read on every row on every render.
   * `.optional()` would make an older server indistinguishable from an undelegated row, and
   * §1b's gate would then fail OPEN — offering a split on a post that already has one.
   *
   * ⚠️ THE PARENT KEY, NEVER A BOOLEAN. `arrangement.model.ts`'s own reason, unchanged:
   * `CD:BOW` can ALSO exist because the CP office asked Bowbazar directly, and folding that
   * into a flag would erase an instruction with the totals still balancing.
   *
   * It discloses nothing new — an allocKey is `<divisionId>:<unitId|*>`, and both ids are
   * already on this row as `allocKey`, `division` and `unit`.
   */
  delegatedFromAllocKey: z.string().nullable(),
  allocatedCount: z.number().int().min(0),
  namedCount: z.number().int().min(0),
  fillState: z.enum(FILL_STATES),
  slots: z.array(fillSlotSchema),
});

export const fillLineSchema = z.object({
  lineKey: z.string(),
  /** Resolved from the post-type or vehicle-type master, or the one-off label itself. */
  label: z.string(),
  shortLabel: z.string(),
  kind: z.enum(['post', 'vehicle', 'adhoc']),
  /**
   * ⚠️ RESOLVED HERE SO THE SHEET NEEDS NO SECOND ROUND TRIP, and so the search's rank
   * filter and the label the operator reads come from one answer. §4.2: a post type is a
   * bucket of acceptable ranks, and that is what drives the name search at fill time.
   */
  eligibleRanks: z.array(rankSummarySchema),
  displayOrder: z.number().int().min(0),
  allocations: z.array(fillAllocationSchema),
  /**
   * ⚠️ [C5c §2h] THIS LINE IS SHARED FROM THIS DUTY POINT ACROSS THE ORDER'S OTHERS — the anchor's
   * marker. Read from the ARRANGEMENT and never stamped on the duty ([C1f §1]'s rule for a marker
   * a re-stamp would strip). Present only when true, so an ordinary sheet does not move a byte.
   */
  shared: z.object({ coversDutyPoints: z.number().int().min(2) }).optional(),
});

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C5c §2h] A COLUMN THIS DUTY IS COVERED FOR, SHARED FROM ANOTHER DUTY POINT — READ-ONLY.
 *
 *  A covered duty carries NO line for a shared column, so `lines` above cannot show it and the
 *  person covering it would be invisible here. This says who that is and where they are named,
 *  and nothing on it is a control: naming happens on the anchor duty's own sheet.
 *
 *  ⚠️ NARROWED EXACTLY AS `lines` IS — the anchor duty's names are listed only for the shares
 *  this caller can see there, and `?division=` narrows them too (FR-5.3.6b). A division that
 *  cannot see the anchor's share is told the column is covered and not who covers it.
 *
 *  ⚠️ `callerHasShare` DECIDES WHETHER "Name them on DP1" IS A LINK. A link to a sheet that would
 *  answer with no lines is a promise the next click breaks.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const fillSharedLineSchema = z.object({
  lineKey: z.string(),
  label: z.string(),
  shortLabel: z.string(),
  kind: z.enum(['post', 'vehicle', 'adhoc']),
  anchor: z.object({ dutyPointTitle: z.string(), ordinal: z.number().int().min(1) }),
  duties: z.array(
    z.object({
      deploymentId: objectIdSchema,
      /** ⚠️ Already humanised by the server (`displaySlotLabel`) — unlike `duty.slotLabel` above. */
      slotLabel: z.string(),
      startsAt: z.iso.datetime(),
      endsAt: z.iso.datetime(),
      callerHasShare: z.boolean(),
      officers: z.array(officerSummarySchema),
    }),
  ),
});

/**
 * ⚠️ A NAME THE ORDER NO LONGER ASKS FOR — SURFACED, NEVER SILENTLY DROPPED.
 *
 * A published arrangement's requirement lines can still be edited and re-stamped. When an
 * edit removes a column, or shrinks a count below a slot that already holds a name, that row
 * moves to `CANCELLED` — and the nodal has to find out from the screen rather than from an
 * officer turning up. It is deliberately outside `lines`: it is not a slot, nothing counts
 * it, and there is nothing to fill.
 */
export const droppedAssignmentSchema = z.object({
  id: objectIdSchema,
  slotRef: slotRefSchema,
  lineLabel: z.string(),
  officer: officerSummarySchema.nullable(),
});

export const fillDutySchema = z.object({
  id: objectIdSchema,
  arrangementId: objectIdSchema,
  reference: z.string(),
  paNumber: z.string().nullable(),
  displayTitle: z.string(),
  /**
   * ⚠️ [P7A §2] THE LEVEL THE ORDER WAS RAISED AT — `arrangementListItemSchema.raisedAtLevel`'s
   * fact and derivation, on the sheet's identity line beside the duty point. One bounded org-unit
   * read per sheet (`loadRaiserLevels`), run in parallel with the command families' read because
   * every fill write returns this sheet. `null` = render no pill.
   */
  raisedAtLevel: z.enum(RAISER_LEVELS).nullable(),
  /**
   * ⚠️ [D4.4 §1a] WHICH DUTY POINT THIS SHEET BELONGS TO, DERIVED ON READ.
   *
   * The sheet has always been correct — `planSlots(duty.requirement_lines)` reads the ONE
   * card's stamped lines — but it never SAID so, and on a three-card order an operator who
   * arrived from the inbox had nothing telling them they were looking at one card's share
   * rather than the whole order's requirement.
   *
   * ⚠️ DERIVED, NEVER STAMPED, for the reason `deployment.service.ts` already records: a
   * stored title goes stale the first time somebody renames the card. Same shared
   * `dutyPointDisplayTitle`, so the inbox, the duty list and this sheet cannot disagree.
   */
  dutyPointTitle: z.string(),
  dutyDate: calendarDateSchema,
  slotLabel: z.string(),
  hasFixedTime: z.boolean(),
  spansMidnight: z.boolean(),
  startsAt: z.iso.datetime(),
  endsAt: z.iso.datetime(),
  locationNames: z.array(z.string()),
  /**
   * ⚠️ [C3a §6] THE DUTY'S OWN STAMPED REPORTING INSTANT — required, nullable.
   * The fill sheet's identity line reads `From 3:00 PM` on a whole-day duty that has one; the
   * gate is `hasFixedTime` beside it, never this value (see `slotTimeLabel`'s header).
   */
  reportingAt: z.iso.datetime().nullable(),
  /** [STAND-DOWN] The duty's own stamped stand-down instant — required, nullable. Whole-day duties only. */
  standDownAt: z.iso.datetime().nullable(),
  isDetached: z.boolean(),
});

/**
 * One addressed org unit's share of one duty — the thing that completes.
 *
 * ⚠️ COMPLETION IS PER ADDRESSEE PER DUTY (FR-5.3.6b), so Central is never blocked by
 * North's slowness and a recurring order can be complete on Monday while Wednesday is still
 * red. The addressee is the UNIT when a line names one and the DIVISION otherwise —
 * `addresseeOf` in `fillRef.util.ts` is the only place that decision is made.
 */
export const fillShareSchema = z.object({
  orgUnit: orgUnitSummarySchema.nullable(),
  division: orgUnitSummarySchema.nullable(),
  allocatedCount: z.number().int().min(0),
  namedCount: z.number().int().min(0),
  fillState: z.enum(FILL_STATES),
  completedAt: z.iso.datetime().nullable(),
  completedBy: officerSummarySchema.nullable(),
  shortfallReason: z.string().nullable(),
});

export const fillSheetSchema = z.object({
  duty: fillDutySchema,
  /** ⚠️ ONLY the lines addressed into the caller's scope. FR-5.3.2, enforced in the query. */
  lines: z.array(fillLineSchema),
  /** ⚠️ [C5c §2h] Present only on a duty covered by a shared column — see `fillSharedLineSchema`. */
  sharedLines: z.array(fillSharedLineSchema).optional(),
  /**
   * ⚠️ [C6a] COMMAND ROLES ASKED OF A DIVISION ON THIS DUTY POINT, ABOVE THE FORCE GRID — present
   * only when there is one the caller can see, so an ordinary sheet does not move a byte (the
   * `sharedLines` idiom). Never a line, never a slot: every figure on this sheet (`allocatedCount`,
   * `namedCount`, `fillState`, the footer's `n of m named`) is computed without it.
   */
  commandFamilies: z.array(fillCommandFamilySchema).optional(),
  shares: z.array(fillShareSchema),
  dropped: z.array(droppedAssignmentSchema),
  allocatedCount: z.number().int().min(0),
  namedCount: z.number().int().min(0),
  fillState: z.enum(FILL_STATES),
  /**
   * ⚠️ [C5 §3] The sheet's OWN roll-up, over the shares in the caller's scope — see
   * `arrangementSchema.closedShort`. Per-SHARE the discriminator has always been on the wire
   * as `fillShareSchema.shortfallReason`, so only the roll-up needed a field.
   *
   * ⚠️ [amended C5b §3] Through `fillOfTotals`, so it carries the narrowed meaning — and the fill
   * footer READS it now; until C5b it printed `All Filled` for every COMPLETE sheet.
   */
  closedShort: z.boolean(),
  /**
   * ⚠️ AN INSTANT, COMPARED AS AN INSTANT (FR-5.3.7). The window closes at the end of the
   * shift; where the duty carries no fixed time it closes at the end of the duty date in
   * Asia/Kolkata. Both are computed on the server from `ends_at`, never from a date string.
   */
  swapWindowClosesAt: z.iso.datetime(),
  swapWindowOpen: z.boolean(),
  /**
   * ⚠️ [C3c §3] THE **NAMING** WINDOW, AND IT IS A SECOND ANSWER RATHER THAN A REPLACEMENT.
   *
   * Naming closes an hour after a fixed-time duty starts; swapping closes when the shift ends.
   * So there is a real interval in which naming is CLOSED and swapping is OPEN, and §3c forbids
   * collapsing them — a single `windowOpen` boolean cannot say which of the two shut.
   *
   * ⚠️ REQUIRED KEYS, NOT OPTIONAL. §6's own test — "does a caller who will never receive a
   * value have to render something?" — is answered YES on every render: the screen draws either
   * the controls or the banner that explains which window closed.
   *
   * ⚠️ AND THERE IS NO THIRD, DERIVED `readOnly` BOOLEAN. That is exactly the collapse §3c
   * forbids, and it would additionally fold in the CAPABILITY, which is the client's own
   * question and one the server already answers with a 403.
   */
  namingWindowClosesAt: z.iso.datetime(),
  namingWindowOpen: z.boolean(),
});

// ── Writes ───────────────────────────────────────────────────────────────────────

export const assignOfficerSchema = z.object({
  slotRef: slotRefSchema,
  officerId: objectIdSchema,
});

/** "Copy from last time", committed. The proposal itself commits nothing until this runs. */
export const bulkAssignSchema = z.object({
  assignments: z.array(assignOfficerSchema).min(1).max(500),
});

export const swapOfficerSchema = z.object({
  officerId: objectIdSchema,
  /**
   * ⚠️ OPTIONAL IN THE CONTRACT, NUDGED IN THE UI (FR-5.3.7). Before completion nothing
   * asks; after completion the screen prompts and the operator may still decline. A
   * required field here would make the prompt a wall, and the reason people give under a
   * wall is "-".
   */
  reason: z.string().trim().max(300).optional(),
});

export const completeShareSchema = z.object({
  /**
   * ⚠️ REQUIRED, AND ONLY REACHED AT PARTIAL. A full share completes ITSELF the moment its
   * last slot fills (FR-5.3.6, closing OD-6), so this endpoint exists for the other case —
   * and a shortfall with no reason is a number nobody upstream can act on.
   */
  shortfallReason: z.string().trim().min(1).max(300),
  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [D4.5 §3e] OPTIONAL, AND ITS ABSENCE IS THE EXISTING BEHAVIOUR VERBATIM.
   *
   *  `completeShares` closes every open share on the duty that the caller can see. That is
   *  right for a nodal, whose scope already implies one division, and wrong for a SUPER_ADMIN
   *  looking at a duty two divisions were asked to fill — §3d gives them a row each, and one
   *  row's button must not close the other's work.
   *
   *  It NARROWS the same verb rather than adding a second one: a separate endpoint would be
   *  two ways to do one thing, and every older caller would keep the wider one for ever.
   *
   *  ⚠️ IT IS NOT VALIDATED HERE AND CANNOT BE. A schema cannot see `Scope`. The check is
   *  `narrowToDivision` in `fulfilment.service.ts`, inside the transaction, against the
   *  shares `requireDuty` has already scope-filtered — and it answers 404 rather than 403,
   *  so "not yours" stays indistinguishable from "does not exist".
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  divisionOrgUnitId: objectIdSchema.optional(),
});

/**
 * ⚠️ [D4.5 §3e] THE FILL SHEET NARROWS BY QUERY PARAMETER, NEVER BY A NEW ROUTE.
 *
 * `/duties/:dutyId` is reached from the inbox, the arrangement detail page and a bookmark,
 * so changing its shape would break every existing link. A query parameter is optional by
 * construction: absent, the sheet is byte-identical to what it was.
 *
 * It must narrow the SHEET and not merely the button — a super admin arriving from the
 * `ND · DP1` row would otherwise see CD's lines above a control that closes only ND's.
 */
/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C1b §3] DELEGATION — A DIVISION SPLITTING ONE ALLOCATION ACROSS ITS OWN UNITS.
 *
 *  ⚠️ **THERE IS NO `divisionOrgUnitId` ON THIS BODY, AND ITS ABSENCE IS THE GUARD.**
 *
 *  §3e requires a division delegating outside itself to be refused SERVER-SIDE rather than
 *  merely absent from the picker. The strongest form of that is not a check: the division
 *  half of every delegated alloc key is COPIED FROM THE SOURCE ALLOCATION, so delegating to
 *  another division is not refused — it is INEXPRESSIBLE. What is then checked is that each
 *  target is a strict descendant of the source's ADDRESSEE, which generalises correctly to a
 *  unit-addressed source (it could only ever split further within itself, never sideways).
 *
 *  ⚠️ **IT CARRIES THE FULL DESIRED SPLIT AND REPLACES, RATHER THAN ADDING.** That makes
 *  un-delegation the SAME verb — a unit at zero, or absent, gets no row (§3d) — so there is
 *  one vocabulary rather than two ([LIFECYCLE]'s rule), and a retry after a dropped response
 *  is a no-op rather than a double split.
 *
 *  ⚠️ **AND `requiredCount` APPEARS NOWHERE.** §3c: this splits ALLOCATION, not requirement.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const MAX_DELEGATION_TARGETS = 60;

export const delegationTargetSchema = z.object({
  unitOrgUnitId: objectIdSchema,
  /** ⚠️ ZERO IS MEANINGFUL — §3d. It removes the row and returns the count to the source. */
  count: z.number().int().min(0).max(9_999),
});

export const delegateAllocationSchema = z
  .object({
    lineKey: z.string().trim().min(1).max(SLOT_REF_MAX_LENGTH),
    allocKey: z.string().trim().min(1).max(SLOT_REF_MAX_LENGTH),
    targets: z.array(delegationTargetSchema).max(MAX_DELEGATION_TARGETS),
  })
  .refine((body) => new Set(body.targets.map((row) => row.unitOrgUnitId)).size === body.targets.length, {
    path: ['targets'],
    message: 'Each unit appears once. Put the whole number on one row.',
  });

/** One unit a source may be split across, with everything the modal needs to bound it. */
export const delegationOptionSchema = z.object({
  orgUnit: orgUnitSummarySchema,
  /** What this unit already holds from this source. */
  delegatedCount: z.number().int().min(0),
  /**
   * ⚠️ THE FLOOR THIS ROW CANNOT BE REDUCED BELOW — the MAXIMUM named across every night of
   * this duty point, not tonight's. One requirement line serves N nights, so tonight's count
   * would strand names on the others (§3b, confirmed with the client).
   */
  namedFloor: z.number().int().min(0),
  /** The night that produced the floor. A number with no date is unactionable. */
  bindingDutyDate: calendarDateSchema.nullable(),
});

export const delegationOptionsSchema = z.object({
  lineLabel: z.string(),
  /** The row being split, as the sheet renders it. */
  sourceLabel: z.string(),
  allocKey: z.string(),
  /** `retained + Σ delegated`. The invariant the modal renders live. */
  pool: z.number().int().min(0),
  retained: z.number().int().min(0),
  /** `pool − maxNamed(source)`. What is actually available to give away. */
  delegable: z.number().int().min(0),
  sourceNamed: z.number().int().min(0),
  bindingDutyDate: calendarDateSchema.nullable(),
  /** ⚠️ How many duties this edit reaches. One requirement line is stamped onto all of them. */
  deploymentCount: z.number().int().min(0),
  options: z.array(delegationOptionSchema),
});

export const delegationOptionsQuerySchema = z.object({
  lineKey: z.string().trim().min(1).max(SLOT_REF_MAX_LENGTH),
  allocKey: z.string().trim().min(1).max(SLOT_REF_MAX_LENGTH),
});

export const fillSheetQuerySchema = z.object({ division: objectIdSchema.optional() });

// ── "Copy from last time" ────────────────────────────────────────────────────────

export const copyProposalRowSchema = z.object({
  slotRef: slotRefSchema,
  officer: officerSummarySchema,
});

/**
 * ⚠️ IT FILLS, IT DOES NOT COMMIT (FR-5.3.4). The operator reviews and adjusts, then saves.
 * For recurring bandobast this saves more operator time than every other affordance on the
 * screen combined, which is exactly why it must not act on its own.
 */
export const copyProposalSchema = z.object({
  /** What it copied FROM, so the screen can say so rather than just filling itself in. */
  source: z
    .object({
      deploymentId: objectIdSchema,
      reference: z.string(),
      dutyDate: calendarDateSchema,
      slotLabel: z.string(),
    })
    .nullable(),
  rows: z.array(copyProposalRowSchema),
});

// ── [COPY-REPEATS] This duty's names, onto the same duty point's other dates ──────

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [COPY-REPEATS] ONE WRITE: THIS DUTY'S NAMES, COPIED ONTO THE SAME DUTY POINT'S OTHER DATES AT
 *  THE SAME TIME SLOT, INTO THE CALLER'S OWN SHARE ON EACH.
 *
 *  ⚠️ IT IS NOT "COPY FROM LAST TIME" RUN BACKWARDS. That one is a READ that fills empty chips on
 *  THIS sheet and commits nothing until Save; this one WRITES other duties, and REPLACES what is
 *  there — which is why it skips a date more than half named, closed short, started or over, and
 *  says so for every date it reached.
 *
 *  ⚠️ THE SAME NARROWING AS `completeShareSchema`'s, AND FOR ITS REASON: absent, the copy acts on
 *  the shares the caller can see; present, on one division's. It is validated in the service,
 *  never here — a schema cannot see `Scope` — and a division the caller cannot see is a 404.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const copyToRepeatsSchema = z.object({
  divisionOrgUnitId: objectIdSchema.optional(),
});

/**
 * ONE SHARE ON ONE OTHER DATE, AND WHAT THE COPY DID TO IT.
 *
 * ⚠️ PER SHARE, NOT PER DATE. A division that handed part of a post to a station holds two shares on
 * every date, and one of them can be the station's finished roster while the other is empty — so
 * the half-named rule is asked of each share, and each is reported.
 *
 * ⚠️ `skipped` IS THE OUTCOME: `null` means copied. One field rather than an outcome beside a
 * reason, so the two cannot disagree. The counts are the share's people slots — never a van, never a
 * position in charge — as they stand after the copy, or as found when it was skipped.
 */
export const copyRepeatsTargetSchema = z.object({
  deploymentId: objectIdSchema,
  dutyDate: calendarDateSchema,
  /** The addressee whose share this is — the fill sheet's `shares[].orgUnit.id`. */
  orgUnitId: objectIdSchema,
  skipped: z.enum(COPY_REPEATS_SKIP_REASONS).nullable(),
  namedCount: z.number().int().min(0),
  allocatedCount: z.number().int().min(0),
});

/** ⚠️ An empty `targets` is an answer: the duty point runs on no other date at this time. */
export const copyRepeatsResultSchema = z.object({
  targets: z.array(copyRepeatsTargetSchema),
});

// ── The officer search ───────────────────────────────────────────────────────────

export const candidateQuerySchema = z.object({
  /** The slot decides BOTH filters — the eligible ranks and the org scope. */
  slotRef: slotRefSchema,
  search: z.string().trim().min(1).max(160).optional(),
});

export const candidateSchema = officerSummarySchema.extend({
  /**
   * ⚠️ [C3c §4a] THE SOURCE'S OWN GL NUMBER, AND IT GOES **HERE** RATHER THAN ON
   * `officerSummarySchema`.
   *
   * That schema has nine consumers and its own header warns there is exactly one builder; it
   * feeds the swap history, the copy proposal, the command roster, `completedBy` — and the
   * PRINTED ORDER SHEET, which is paper that leaves the building. None of them asked for a GL
   * number, and widening it would also make `loadOfficerSummaries` select `meta` for all of them.
   *
   * ⚠️ REQUIRED KEY, NULLABLE VALUE. §6's test — "does a caller who will never receive a value
   * have to render something?" — is YES: the row draws the number beside the rank, or nothing
   * there. `.optional()` would make an older server indistinguishable from an officer who has
   * no GL number.
   *
   * ⚠️ AND IT COSTS NO QUERY. `listCandidates` already reads the whole officer document with no
   * `.select()`, so `meta.gl_no` was in hand before this field existed.
   */
  glNo: z.string().nullable(),
  /** True when the officer already holds another slot on THIS duty. Never a clash check. */
  alreadyOnThisDuty: z.boolean(),
});

export const candidateListSchema = z.object({
  /** The line's own label and ranks, so the sheet can say what it is filtering by. */
  lineLabel: z.string(),
  eligibleRanks: z.array(rankSummarySchema),
  scopeLabel: z.string(),
  /** ⚠️ BOUNDED, and never paginated: a search sheet with a page two is a sheet nobody uses. */
  items: z.array(candidateSchema),
});

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C3c §4b] HOW BUSY HAS THIS OFFICER BEEN? — 90 days, and NOT through the search API.
 *
 *  ⚠️ IT IS A SEPARATE ENDPOINT BECAUSE THE SEARCH PATH MUST STAY FAST. That aggregation on
 *  `GET /duties/:id/candidates` would run on every keystroke, which is exactly the
 *  responsiveness [P4] built the search sheet to protect — twelve names in ninety seconds.
 *  Names render first; the counts fill in behind them.
 *
 *  ⚠️ 90 DAYS ENDING YESTERDAY, AND TODAY IS EXCLUDED DELIBERATELY. Today's duties are in
 *  progress or not yet worked, so including a partial day makes the number move during a
 *  sitting for reasons the operator cannot see. "Yesterday" is a completed IST business day.
 *
 *  ⚠️ AND THE WINDOW IS 91 CALENDAR DAYS INCLUSIVE — `[yesterday − 90, yesterday]`, which is the
 *  brief read literally. Said out loud so nobody "fixes" the off-by-one; the label reads
 *  "Last 90 days", so nothing on screen has to be interpreted.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const DUTY_COUNT_WINDOW_DAYS = 90;

/**
 * ⚠️ THE SERVER'S BOUND IS THE CANDIDATE LIST'S OWN BOUND, NOT THE CLIENT'S BATCH SIZE. How many
 * officers a sheet may ask about is a fact about that list; how many it asks about AT ONCE is a
 * rendering decision, and a server limit that mirrored it would be a second statement of one.
 */
export const MAX_DUTY_COUNT_IDS = 30;

/*
 * ⚠️ [amended C3c.1] `from` AND `to` ARE ECHOED AND **READ BY NOBODY**, AND THAT IS RECORDED
 * RATHER THAN QUIETLY LEFT.
 *
 * The stated justification was that echoing them lets "the client label the number without
 * re-deriving a date in a zone it does not own" — and the label hardcodes "the last 90 days",
 * so nothing reads either field. §9's no-dead-tooling rule is what removed
 * `recentDeploymentCount` from this same DTO one section up, for the same reason.
 *
 * ⚠️ THEY STAY, AND THE REASON IS DIFFERENT FROM THAT ONE. `recentDeploymentCount` was a WRONG
 * number that a renderer might one day have believed; these two are the SERVER'S OWN STATEMENT
 * OF THE WINDOW IT MEASURED, which is what makes the count interpretable at all — the audit of a
 * screenshot, the next label that wants a date, and the integration test that pins the
 * 91-inclusive-day arithmetic all read them. `fulfilment.test.ts` is a real consumer and is
 * named here so the absence of a UI one is a decision rather than an oversight.
 */

export const dutyCountsQuerySchema = z.object({
  /**
   * ⚠️ THE SAME SLOT THE CANDIDATE LIST TAKES, so this can call the SAME `requireFillSlot`. That
   * gives 404-not-403 for another division's duty for free, and — the part that matters — the
   * same `addressedUnitIds` officer set the sheet was allowed to offer in the first place.
   */
  slotRef: slotRefSchema,
  /**
   * ⚠️ `z.preprocess`, AND IT ACCEPTS BOTH WIRE FORMS — a COMMA-JOINED string and repeated keys.
   *
   * `listArrangementsQuerySchema.status` and `dashboardQuerySchema.tiles` set the precedent for
   * the preprocess itself. The comma form is here because the browser client's `query` option
   * takes SCALARS ONLY — `buildUrl` uses `params.set`, not `append` — so a batch of five ids
   * cannot be sent as repeated keys without widening the shared HTTP client for one caller.
   *
   * ⚠️ COMMA-JOINING IS PROVABLY UNAMBIGUOUS HERE, which is why it is acceptable rather than
   * merely convenient: `objectIdSchema` is 24 hex characters, so an id can never contain the
   * separator. That would not be true of a free-text parameter, and this reasoning does not
   * generalise to one.
   */
  officerIds: z.preprocess(
    (value) => (typeof value === 'string' ? value.split(',').filter(Boolean) : value),
    z.array(objectIdSchema).min(1).max(MAX_DUTY_COUNT_IDS),
  ),
  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [C5 §11b] THE MONTHLY BREAKDOWN, ASKED FOR EXPLICITLY — A FLAG, NOT A SECOND ROUTE.
   *
   *  §11 wants a hover popover showing this month, last month and the month before. That is the
   *  same question this endpoint already answers, at a different grain, about the same officer
   *  set — so it reuses `requireFillSlot` and `addressedUnitIds`, which is the membership check
   *  that makes this a FACT rather than an ORACLE. A sibling route would have had to repeat
   *  both, and would have needed an entry in `docs/api/pams-api.postman_collection.json` that
   *  `apiCollectionCoverage.test.ts` enforces.
   *
   *  ⚠️ §11c: THE SEARCH PATH IS BYTE-IDENTICAL WITHOUT IT. The candidate list omits the flag,
   *  so its window, its `$group` and its response keys do not move. [C3c §4b] kept the 90-day
   *  count off the keystroke path deliberately, and the hover asks for ONE officer.
   *
   *  ⚠️ AND IT IS `stringbool`, NOT a bare `z.boolean()`. A query parameter arrives as a string;
   *  `showLifecycleSchema` and the list filters set the idiom.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  breakdown: z.stringbool().optional(),
});

export const dutyCountsSchema = z.object({
  /**
   * ⚠️ THE WINDOW IS ECHOED, so the client labels the number without re-deriving a date in a zone
   * it does not own. `notificationsSchema` does the same, for the same reason.
   */
  from: calendarDateSchema,
  to: calendarDateSchema,
  /**
   * ⚠️ AN ID THE CALLER MAY NOT ASK ABOUT IS SIMPLY ABSENT — never an error, and never a zero.
   * `arrangementSummaries`' rule: one half asking about the other must learn nothing at all, not
   * even that it was refused. A zero would be a claim about an officer we declined to look at.
   */
  counts: z.array(z.object({ officerId: objectIdSchema, count: z.number().int().min(0) })),
  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [C5 §11b] THIS MONTH, LAST MONTH AND THE MONTH BEFORE — present ONLY when asked for.
   *
   *  ⚠️ OPTIONAL, NOT A REQUIRED EMPTY ARRAY. [P6d]'s test: the key's PRESENCE is the signal
   *  that a different code path ran, and the candidate list — which fetches for up to thirty
   *  officers on every page — must not carry it at all. `undefined` is omitted by
   *  `JSON.stringify`, so its bytes do not move.
   *
   *  ⚠️ `month` IS `YYYY-MM`, AND THAT IS AN **IST** MONTH WITH NO TIMEZONE MATHS ANYWHERE.
   *  `assignments.duty_date` is already an IST business-date string ([P3a]: lexicographic order
   *  is chronological order), so a `YYYY-MM` prefix IS the month — `$substrBytes` on the server
   *  and a string comparison here. The alternative would have been `$dateToString` with a
   *  timezone over an instant, which is the crossing [IST-FIX] found broken twice.
   *
   *  ⚠️ AND EVERY REQUESTED MONTH GETS A ROW, INCLUDING A ZERO — `counts`' own rule one field
   *  up: a missing row renders as a skeleton that never resolves, and "nobody used this officer
   *  in March" is the most useful thing this endpoint says.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  months: z
    .array(
      z.object({
        officerId: objectIdSchema,
        buckets: z.array(z.object({ month: z.string(), count: z.number().int().min(0) })),
      }),
    )
    .optional(),
});

// ── The inbox ────────────────────────────────────────────────────────────────────

/**
 * ⚠️ [D4.5 §3a] TODAY THROUGH THE NEXT SEVEN DAYS, AND THE SERVER OWNS IT.
 *
 * The inbox used to default to today alone. Shared rather than written twice, because the
 * hint the screen renders ("today and the next 7 days") and the range the query applies are
 * one fact — and two copies of it drift the first time somebody tunes one.
 */
export const INBOX_DEFAULT_WINDOW_DAYS = 7;

/**
 * ⚠️ [D4.5 §3d] A BOUND, BECAUSE THE LIST IS NOW AN AGGREGATION.
 *
 * §7 requires every list query to be bounded. `$group` is a blocking stage, so the working
 * set is whatever the `$match` selects rather than whatever one page holds — and a caller
 * asking for five years of duties would build that set in memory before paginating it. The
 * `$match` is index-served and this is what keeps it selective.
 *
 * ⚠️ IT THROWS RATHER THAN TRUNCATING, and it is a server constant rather than something a
 * caller can raise — `getOrgUnitCounts`' rule. A silently narrowed window would under-report
 * duties, which on this screen means a division is never asked to fill one.
 */
export const INBOX_MAX_WINDOW_DAYS = 92;

export const INBOX_SORT_FIELDS = ['fillState', 'startsAt', 'dutyDate'] as const;
export type InboxSortField = (typeof INBOX_SORT_FIELDS)[number];

/**
 * ⚠️ [D4.5 §3d] THE DEFAULT SORT IS `dutyDate`, AND THAT IS A RECORDED DEPARTURE FROM
 * FR-5.3.1, WHICH SAYS "grouped by status".
 *
 * It was `fillState` — red, then amber, then green — and that is the right default for a
 * list of TODAY. §3a widens the window to eight days, and over eight days an operator works
 * through nights rather than through colours: status-first puts day 8's unfilled duties
 * above today's half-filled ones, which is the wrong thing at the top of a landing screen.
 *
 * ⚠️ NOTHING ABOUT THE COLOUR SEMANTICS CHANGES. `fillState` is still a sort option, the
 * `StatusDot` still carries red/amber/green/locked, and `fill_rank` still orders it.
 */
export const listInboxQuerySchema = createPaginationSchema(INBOX_SORT_FIELDS, 'dutyDate')
  .extend({
    /**
     * ⚠️ BOTH OPTIONAL, AND "TODAY" IS THE SERVER'S DEFAULT RATHER THAN THE CLIENT'S.
     *
     * FR-5.3.1 defaults the inbox to today. Writing today's date into the URL on mount would
     * make the back button strange, bake a date into a shared link, and open a link shared at
     * 23:59 on yesterday. The same call `kind.default('STANDARD')` already makes.
     */
    dateFrom: calendarDateSchema.optional(),
    dateTo: calendarDateSchema.optional(),
    fillState: z.enum(FILL_STATES).optional(),
    divisionOrgUnitId: objectIdSchema.optional(),
    policyId: objectIdSchema.optional(),
    /**
     * ⚠️ [C1d §2] THE FIFTH LIST ON THE ARCHIVE AXIS, AND THE FIRST WHOSE SUBJECT IS NOT THE
     * ARCHIVED RECORD ITSELF.
     *
     * `allocation_fills.archived_at` is CASCADED from the order, so the inbox answers exactly
     * the three-valued question the arrangement list and the four master lists already answer,
     * through the same `showLifecycleSchema` and the same `archivedFilter`. A two-member enum
     * scoped to this one route would be a second archive vocabulary — [LIFECYCLE]'s named
     * failure mode, where the forgotten one fails silently.
     *
     * ⚠️ AND IT HAS TO BE DECLARED HERE OR IT DOES NOTHING. The middleware STRIPS unknown
     * keys, so a client that shipped the control and genuinely sent `?show=archived` would
     * get an unchanged list with no error, no 422 and no log line — which is verbatim what
     * [D4.4] found `listArrangementsQuerySchema` doing for a whole release.
     */
    show: showLifecycleSchema,
    /**
     * ⚠️ [C5c §7a] THE ASSIGN PA PAGE SEARCHES NOW — the order's PA number, reference, title or a
     * place, through the same `arrangementSearchPredicate` the arrangement list uses. `show`'s
     * reason one key up applies verbatim: undeclared, the middleware would STRIP it and a client
     * shipping the box would get the unfiltered inbox back with no error.
     *
     * ⚠️ `.trim().min(1)`, the list's own rule — so a blank term is a 422, and every client sends
     * the term only when its trimmed value is non-empty.
     */
    /** [PLACE-500] The same predicate reads duty places, so the term may be as long as one. */
    search: z.string().trim().min(1).max(ARRANGEMENT_LIMITS.PLACE_NAME).optional(),
  })
  .extend({ sortOrder: z.enum(['asc', 'desc']).default('asc') });

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [D4.5 §3d] ONE ROW PER DIVISION × DUTY POINT × NIGHT.
 *
 *  It was one row per ADDRESSED SHARE — per `allocation_fills` document — so a division
 *  addressed both at its own level and at one of its stations produced two rows for one
 *  night's work, and neither said which duty point it belonged to.
 *
 *  ⚠️ `allocation_fills` IS UNCHANGED. Its grain, its unique index and its per-addressee
 *  completion are exactly what FR-5.3.6b requires; this is a GROUPING on read. The units
 *  addressed within the division are combined into `addressees`, and a `null` member is the
 *  division's own share — the common case, which renders as `Any`.
 *
 *  ⚠️ `id` IS GONE. The row is no longer a document, and an `objectIdSchema` id naming
 *  nothing is a lie a consumer will eventually dereference. The screen keys on
 *  `deploymentId` + `divisionOrgUnitId`, which is what the row IS.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const inboxRowSchema = z.object({
  deploymentId: objectIdSchema,
  /** ⚠️ [D4.5 §3d] The other half of the row's identity. Never null — every share has one. */
  divisionOrgUnitId: objectIdSchema,
  /** 1-based, derived on read from the order's own card order — never stored. */
  dutyPointOrdinal: z.number().int().min(1),
  /** Derived through `dutyPointDisplayTitle`, so four screens cannot disagree about a card. */
  dutyPointTitle: z.string(),
  /*
   * ⚠️ [C3c §2c] `arrangementId`, `reference`, `paNumber`, `displayTitle`, `kind` AND
   * `arrangementVisible` MOVED UP TO `inboxGroupSchema`.
   *
   * A field carried on BOTH the group and its rows is two answers to one question, and the row's
   * copy is the one that goes stale. They were per-row only because the list was flat.
   */
  dutyDate: calendarDateSchema,
  slotLabel: z.string(),
  /** ⚠️ [D4.5 §3d] The TIME alone — `slotLabel` leads with the date, which now has a column. */
  timeLabel: z.string(),
  hasFixedTime: z.boolean(),
  spansMidnight: z.boolean(),
  startsAt: z.iso.datetime(),
  endsAt: z.iso.datetime(),
  locationNames: z.array(z.string()),
  /**
   * ⚠️ [D4.5 §3d] EVERY UNIT ADDRESSED WITHIN THIS DIVISION ON THIS DUTY, COMBINED.
   *
   * It replaces a single `orgUnit`, which could only ever name ONE addressee — so a division
   * asked at its own level AND at one of its stations produced two rows for one night's
   * work. `null` is the division's own share ("any unit under this division"), the common
   * case and the fast path.
   *
   * ⚠️ ORDERED BY THE SERVER — `null` first, then by short code. `$addToSet` has no defined
   * output order, and two clients must not disagree about whether it reads `A, B, Any` or
   * `Any, B, A`.
   */
  addressees: z.array(orgUnitSummarySchema.nullable()),
  /**
   * ⚠️ [D4.4 §1c] THE DIVISION, ALWAYS — SEPARATELY FROM THE ADDRESSEE.
   *
   * `orgUnit` alone cannot answer "which division is this asking?": on a division-only
   * allocation it IS the division, and on a station-level one it is the station, with the
   * division nowhere on the row. So the inbox — the nodal's landing screen — showed a list
   * of duties with no indication of whose force each one wanted. The column exists on
   * `allocation_fills` already (`division_org_unit_id`, required and indexed); it had
   * simply never been put on the wire.
   */
  division: orgUnitSummarySchema.nullable(),
  allocatedCount: z.number().int().min(0),
  namedCount: z.number().int().min(0),
  fillState: z.enum(FILL_STATES),
  /**
   * ⚠️ [C5b §3] `closedShort` BESIDE `fillState`, EXACTLY AS ON `fillSheetSchema`.
   *
   * The inbox is the one list a nodal WORKS from, and it had no way to say which of COMPLETE's
   * two causes it held — so a division that closed short at nobody named read `0 of 12 · All
   * Filled 🔒` here while the order's own record said `Closed short`. `CLOSED_SHORT_EXPRESSION`
   * computes it in the same pipeline the rank comes from, so the word and the sort agree.
   * Required, not optional: a row a client cannot word is the defect this field closes.
   */
  closedShort: z.boolean(),
  /**
   * ⚠️ [P7A §4] WHY THE ROW CLOSED SHORT — the distinct reasons of its DECLARED shares, sorted.
   *
   * `closedShort` says that a division replied; this says what it said, beside the duty row, so a
   * nodal reading `0 of 12 · Closed short` is not left to ring somebody to ask. Non-empty only when
   * `closedShort` and something was asked, so a vehicle's words never reach the screen. Several when
   * a division and one of its units closed separately with different words — the row is division ×
   * duty and merges them. `DECLARED_REASONS` in the same pipeline as the rank, never a second read.
   * Required, possibly `[]`.
   */
  shortfallReasons: z.array(z.string().min(1).max(300)),
  completedAt: z.iso.datetime().nullable(),
});

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C3c §2] ONE ARRANGEMENT, AND THE DUTY ROWS BENEATH IT THAT THIS CALLER MAY SEE.
 *
 *  ⚠️ §2d — A PAGE IS N ARRANGEMENTS NOW, NOT N DUTIES, SO `total` COUNTS ARRANGEMENTS.
 *  That is a change to what the envelope MEANS and it is recorded rather than absorbed:
 *  `web/src/lib/inboxGrouping.ts`'s fourth refusal of nesting was "it changes nothing — no sort,
 *  no filter, no `total`, no pagination", and that is now deliberately false.
 *
 *  ⚠️ §2e — AND THE HEADER CANNOT REVEAL A DUTY ROW THE FLAT LIST DID NOT. The group is a
 *  `$group` OVER the rows `shareMatch(scope, …)` admitted, so it exists if and only if at least
 *  one row the caller was already entitled to exists beneath it, and every count on it is a sum
 *  over exactly those rows. `rows` is `.min(1)` for that reason: an EMPTY group is
 *  unrepresentable, not merely unproduced.
 *
 *  ⚠️ THE ARRANGEMENT'S NAME WAS ALREADY ON EVERY INBOX ROW. §2 prints it once instead of N
 *  times; it discloses nothing that was not already on the screen.
 *
 *  ⚠️ [amended C5c §10] ONE FIELD NOW BREAKS THAT SENTENCE, AND IT IS SAID RATHER THAN HIDDEN.
 *  `raisedAtLevel` ([amended P7A §2], formerly the boolean `raisedAtCommissionerate`) is a NEW
 *  fact from a NEW read (the raiser's org unit), so "nothing
 *  that was not already on the screen" is no longer true of the header as a whole. What §2e still
 *  guarantees is unchanged: every COUNT is a sum over the caller's own rows, and a group exists only
 *  above one of them. The raiser's level is admitted on its own argument — see the field.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const inboxGroupSchema = z.object({
  arrangementId: objectIdSchema,
  reference: z.string(),
  paNumber: z.string().nullable(),
  displayTitle: z.string(),
  /** ⚠️ Present so a round-the-clock order can be RECOGNISED, never so it can be treated
   *  differently: P3b.1's claim is that it is an ordinary arrangement, and this screen is
   *  not the place that stops being true. */
  kind: z.enum(ARRANGEMENT_KINDS),
  /**
   * ⚠️ [C1e §2f] NORMALLY TRUE, AND FALSE AFTER A REPARENT — moved up from the row, because it
   * is a fact about the ORDER and the group header is what links to it.
   */
  arrangementVisible: z.boolean(),
  /** The span of this arrangement's duty rows WITHIN THE WINDOW — never its whole life. */
  dutyDateFrom: calendarDateSchema,
  dutyDateTo: calendarDateSchema,
  /** Summed over the caller's own rows. ⚠️ Never over the order's other divisions — see §2e. */
  allocatedCount: z.number().int().min(0),
  namedCount: z.number().int().min(0),
  fillState: z.enum(FILL_STATES),
  /** ⚠️ [C5b §3] The group's own `closedShort`, one grouping level up — see `inboxRowSchema`. */
  closedShort: z.boolean(),
  /**
   * ⚠️ [amended P7A §2] AT WHICH LEVEL WAS THE ORDER RAISED? — see
   * `arrangementListItemSchema.raisedAtLevel`: the same fact, the same derivation (the RAISER's
   * position — `raised_at_org_unit_id ?? created_by_org_unit_id` — never the addressee, and the
   * label only as a belt), so an order cannot carry one pill on one page and another on the next.
   * Required, nullable, for the same reason; it replaces [C5c §10]'s boolean.
   *
   * ⚠️ AND IT IS STATED EVEN WHEN `arrangementVisible` IS FALSE — a recorded decision, NOT §2e. The
   * header of a non-openable group already shows the order's title and PA number to a caller who
   * holds a share on it; the raiser's level is a fact at the same sensitivity. Forcing `null` there
   * would make `null` mean two things — "no resolvable raiser" and "you cannot open this" — and the
   * second already has its own key.
   */
  raisedAtLevel: z.enum(RAISER_LEVELS).nullable(),
  /**
   * ⚠️ THE TRUE NUMBER OF DUTY ROWS, WHICH MAY EXCEED `rows.length`.
   *
   * `rows` is capped server-side so one arrangement cannot blow the 16 MB response cap. The cap
   * is STATED rather than silent: a header can read "40 of 192 — narrow the dates", which is
   * `outstandingByArrangement`'s own "a bell reading 12 above eight rows is honest".
   */
  dutyRowCount: z.number().int().min(1),
  rows: z.array(inboxRowSchema).min(1),
});

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [PRE-P6.2] ONE DUTY'S ASSIGNMENT HISTORY — WHO CHANGED WHO, WHEN, AND WHY.
 *
 *  `audit_logs` has recorded every name, swap, removal and completion since [P4], keyed on
 *  `(DEPLOYMENT, deploymentId)`, and no client had ever read it: `GET /audit` is SUPER_ADMIN
 *  via `audit.read` AND — [AUDIT-FLAG] — the `access_analytics` flag, while the people who do the
 *  swapping are divisional nodals. A trail
 *  the actors cannot see is a trail that only settles arguments after they have escalated.
 *
 *  ⚠️ THE STORED ROW HOLDS IDS AND THIS DTO HOLDS NAMES, WHICH IS THE RULE RATHER THAN AN
 *  EXCEPTION TO IT. `auditFill`'s own header: "an officer id resolves to a person for anyone
 *  entitled to ask; a name in a log line does not un-write". Resolving at READ time, behind
 *  the duty's own scope check, is exactly the entitlement that sentence describes.
 *
 *  ⚠️ IT IS THE WHOLE FILL HISTORY, NOT ONLY SWAPS. A swap log that cannot say who was
 *  originally named is half a story — "R. Das replaced A. Sen" means nothing without the line
 *  above it saying A. Sen was named there in the first place.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const DUTY_HISTORY_ACTIONS = [
  'ASSIGNMENT_NAMED',
  'ASSIGNMENT_SWAPPED',
  'ASSIGNMENT_REMOVED',
  'ALLOCATION_COMPLETED',
] as const;
export type DutyHistoryAction = (typeof DUTY_HISTORY_ACTIONS)[number];

export const dutyHistoryEntrySchema = z.object({
  id: objectIdSchema,
  action: z.enum(DUTY_HISTORY_ACTIONS),
  at: z.iso.datetime(),
  /** Who did it. `null` only if the actor's officer row has since gone. */
  actor: officerSummarySchema.nullable(),
  /** The slot, for everything except a completion — which is about a whole share. */
  slotRef: z.string().nullable(),
  /** Who was in the slot before, and who is in it now. Either may legitimately be absent. */
  before: officerSummarySchema.nullable(),
  after: officerSummarySchema.nullable(),
  /** A swap reason, or a shortfall reason on a completion. */
  reason: z.string().nullable(),
});

export const listDutyHistoryQuerySchema = createPaginationSchema(['at'] as const, 'at');
